import { Fragment, type ReactElement } from 'react'

import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TableCell from '@mui/material/TableCell'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import KNDataTableMenu from './KNDataTableMenu'
import type { KNDataTableRowProps } from './types'

const KNDataTableRow = <T extends object>({
  columns,
  row,
  primaryActions = [],
  secondaryActions = [],
}: KNDataTableRowProps<T>): ReactElement => {
  const visiblePrimaryActions = primaryActions.filter((action) => action.isVisible?.(row) ?? true)
  const visibleSecondaryActions = secondaryActions.filter((action) => action.isVisible?.(row) ?? true)

  // NOTE: component isn't wrapped in <TableRow> due to react-virtuoso
  return (
    <>
      {columns.map((column) => (
        <TableCell key={column.name} sx={{ verticalAlign: 'top' }}>
          {column.getValue ? (
            column.getValue(row)
          ) : (
            <Typography noWrap color="inherit">
              {row[column.name]}
            </Typography>
          )}
        </TableCell>
      ))}
      <TableCell>
        {(visiblePrimaryActions.length > 0 || visibleSecondaryActions.length > 0) && (
          <Stack direction="row" justifyContent="end" sx={{ marginY: '-6px' }} data-guide="datatable-actions">
            {visiblePrimaryActions.map((action) => (
              <Fragment key={action.name}>
                <Tooltip title={action.label} enterDelay={500} placement="top" arrow>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      event.stopPropagation()
                      action.onClick?.(row)
                    }}
                    size="small"
                    className="hover-opacity"
                    sx={{ color: 'primary.main' }}
                  >
                    {action.icon}
                  </IconButton>
                </Tooltip>
              </Fragment>
            ))}
            {visibleSecondaryActions.length > 0 && (
              <KNDataTableMenu row={row} actions={visibleSecondaryActions} rightAligned />
            )}
          </Stack>
        )}
      </TableCell>
    </>
  )
}

export default KNDataTableRow
